import dayjs, { OpUnitType } from 'dayjs/esm'
import type { SwitchingCategoryTypeEnum } from '@gridcon-energy/schaltungstool-backend'
import { DateRangeNullable } from '~/lib/gantt'
import type { DayjsRange } from '~/lib/gantt'
import { SwitchingEventsEnum } from '~/lib/activity'
import { useStorage } from '@vueuse/core'
import { get as _get } from 'lodash-es'
import { ComponentInternalInstance, computed, onUnmounted, reactive, Ref, toRaw, toRef } from 'vue'
import { LogDetailed } from '~/helper/log'
import { keys } from '~/helper/data-transform'

export type DateRangeFilter = DateRangeNullable & { switchingEvents: SwitchingEventsEnum | null }

export type ActivityDataFilterStorage = {
  createdAt?: number
  // contains id
  project: number[]
  equipment: number[]
  equipmentType: string[]
  subProject: number[]

  voltageLevel: string[]
  switchingCategory: SwitchingCategoryTypeEnum[]
  datesRange: DateRangeFilter
  equipmentConflicts: boolean
}

/**
 * Filters an item by a list of values
 */
export function filterByList<T>(
  item: T,
  list: (object | number | string)[],
  propertyKey: string,
  strict = false,
): boolean {
  const value = _get(item, propertyKey)
  // LogDetailed(`Expected ${value} (${propertyKey}) to be in ${list.join(', ')}`)

  // case-insensitive + equals
  if (strict) {
    return list.includes(value)
  }

  if (typeof value === 'string') {
    return !!value.match(new RegExp(list.join('|'), 'gi'))
  }
  // use loose comparison

  return list.some((predicate) => value == predicate)
}

/**
 * Filters items by a list of values
 */
export const filterItemsByList = <T>(
  items: T[],
  list: (object | number | string)[],
  propertyKey: string,
  propertySubKey?: string,
  strict = false,
): T[] => {
  if (list.length === 0) {
    return items
  }

  // default array filtering, when propertySubKey doesn't exist
  if (!propertySubKey) {
    return items.filter((item) => filterByList(item, list, propertyKey, strict))
  }

  // array sub filtering based on propertySubKey
  return items
    .map((item) => {
      const value = _get(item, propertyKey)
      if (!Array.isArray(value)) {
        console.error(`"${propertyKey}" is not an array of`, item)
        return item
      }
      const filteredSubItems = value.filter((item: Record<string, unknown>) =>
        filterByList(item, list, propertySubKey, strict),
      )
      return {
        ...item,
        [propertyKey]: filteredSubItems,
      }
    })
    .filter((item) => {
      const value = _get(item, propertyKey)
      if (Array.isArray(value)) {
        return value.length
      }
      return true
    })
}

/**
 * Filters items by a date range
 */
export function filterItemsByDateRange<T, P extends keyof T>(
  rows: T[],
  keyEnd: keyof T | string,
  keyStart: keyof T | string,
  range: DateRangeNullable,
  propertyKey?: P,
) {
  if (range.start === null && range.end === null) {
    return rows
  }
  const dateRangeFilter = createDateRangeFilter(keyStart, keyEnd, range)

  if (!propertyKey) {
    return rows.filter(dateRangeFilter)
  }

  // Filter values of propertyKey
  const rowsFilteredByPropertyKey = rows.map((row) => {
    const value = _get(row, propertyKey)
    if (!Array.isArray(value)) {
      console.error(`"${String(propertyKey)}" is not an array of`, row)
    }

    // shallow copy to not overwrite propertyKey value
    return {
      ...row,
      [propertyKey]: value.filter(dateRangeFilter),
    }
  })

  // Filter rows where propertyKey array is empty
  return rowsFilteredByPropertyKey.filter((item) => {
    const value = _get(item, propertyKey)
    if (Array.isArray(value)) {
      return value.length
    }
    return true
  })
}

/**
 * Default values for start/end to compare against NULL
 */
const farPast = dayjs('1000-01-01')
const farFuture = dayjs('2999-12-31')

/**
 * Return a filter dates which intersect/overlap with filter
 */
function createDateRangeFilter<T>(keyStart: keyof T | string, keyEnd: keyof T | string, filter: DateRangeNullable) {
  const base: DayjsRange = {
    start: filter.start ? dayjs(filter.start) : farPast,
    end: filter.end ? dayjs(filter.end) : farFuture,
  }
  return function dateRangeFilter(item: T) {
    const itemStart: string | null = _get(item, keyStart)
    const itemEnd: string | null = _get(item, keyEnd)

    // Ermittle die tatsächlichen Start- und Enddaten für Ereignis.
    const event: DayjsRange = {
      start: itemStart ? dayjs(itemStart) : farPast,
      end: itemEnd ? dayjs(itemEnd) : farFuture,
    }

    return dateRangeOverlap(base, event)
  }
}

/**
 * Check whether the time periods overlap.
 * One time period overlaps with another if its start is before the end of the other
 * and its end after the start of the other.
 */
function dateRangeOverlap(a: DayjsRange, b: DayjsRange) {
  return a.start.isBefore(b.end) && a.end.isAfter(b.start)
}

/**
 * Check if b is contained in a
 */
export function dateRangeBetween(a: DayjsRange, b: DayjsRange, unit?: OpUnitType) {
  return b.end.isSameOrBefore(a.end, unit) && b.start.isSameOrAfter(a.start, unit)
}

function filterDefaults(): ActivityDataFilterStorage {
  return {
    createdAt: new Date().getTime(),
    project: [],
    equipment: [],
    equipmentType: [],
    subProject: [],
    switchingCategory: [],
    voltageLevel: [],
    datesRange: {
      start: null,
      end: null,
      switchingEvents: null,
    },
    equipmentConflicts: false,
  }
}

export type ActivityDataFilter = ReturnType<typeof useActivityDataFilter>
export default function useActivityDataFilter(activityId: number) {
  const inStorage = useStorage<ActivityDataFilterStorage>(`${activityId}.data-filter`, filterDefaults())

  function reset<T extends keyof ActivityDataFilterStorage = keyof ActivityDataFilterStorage>() {
    for (const [key, value] of Object.entries(filterDefaults())) {
      inStorage.value[key as T] = value as ActivityDataFilterStorage[T]
    }
  }

  /**
   * We want to track the filters in use to hide UI-filters which are not used in current view.
   */
  const filtersInUse = reactive<Record<keyof ActivityDataFilterStorage, number>>({
    createdAt: 0,
    project: 0,
    equipment: 0,
    equipmentType: 0,
    subProject: 0,
    switchingCategory: 0,
    voltageLevel: 0,
    datesRange: 0,
    equipmentConflicts: 0,
  })

  /**
   * Track filter usage in UI-Components where data is actually filtered
   */
  function useFilter<K extends keyof ActivityDataFilterStorage>(
    key: K,
    vm?: ComponentInternalInstance,
  ): Readonly<Ref<ActivityDataFilterStorage[K]>>
  function useFilter(): Readonly<Ref<ActivityDataFilterStorage>>
  function useFilter(key?: keyof ActivityDataFilterStorage, vm?: ComponentInternalInstance) {
    if (!key) {
      console.warn('Be sure to actually use every key... ☝🏻')
      for (const key of keys(filtersInUse)) {
        if (key in filtersInUse) {
          filtersInUse[key]++
        }
      }
      onUnmounted(() => {
        for (const key of keys(filtersInUse)) {
          if (key in filtersInUse) {
            filtersInUse[key]--
          }
        }
      }, vm)
      return toRef(() => inStorage.value)
    }
    filtersInUse[key]++
    onUnmounted(() => {
      filtersInUse[key]--
    }, vm)
    return toRef(() => inStorage.value[key])
  }

  /**
   * Returns the number of active filters.
   */
  const filterActiveCount = computed(() => {
    let count = 0
    for (const [keyS, value] of Object.entries(inStorage.value)) {
      const key = keyS as keyof ActivityDataFilterStorage
      if (key === 'createdAt') {
        continue
      }
      if (Array.isArray(value)) {
        if (value.length) {
          count++
        }
        continue
      }
      if (typeof value === 'object') {
        count = count + Object.values(value).filter((val) => val != null).length
        continue
      }

      count = count + (value ? 1 : 0)
    }
    return count
  })

  return {
    inStorage: inStorage,
    reset,
    filterActiveCount,
    useFilter: useFilter,
    filtersInUse: filtersInUse,
  }
}
